import {
  combineReducers,
  configureStore,
  type Reducer,
} from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import type { TypedUseSelectorHook } from "react-redux";
import { useDispatch, useSelector } from "react-redux";

import userSlice from "../redux/userSlice";
import { apiSlice } from "./api/baseApi";

interface RootStateShape {
  userSlice: ReturnType<typeof userSlice>;
  [apiSlice.reducerPath]: ReturnType<typeof apiSlice.reducer>;
}

const rootReducer: Reducer<RootStateShape> = combineReducers({
  userSlice,
  [apiSlice.reducerPath]: apiSlice.reducer,
});

export type RootState = RootStateShape;

// Keep RTK's frame/fallback ordering in tests, while retaining the cancellation
// function before jsdom removes its globals during teardown.
const queueTestFrameNotification = (notify: () => void) => {
  if (
    typeof window === "undefined" ||
    typeof window.requestAnimationFrame !== "function" ||
    typeof window.cancelAnimationFrame !== "function"
  ) {
    setTimeout(notify, 10);
    return;
  }

  const ownerWindow = window;
  const requestFrame = ownerWindow.requestAnimationFrame.bind(ownerWindow);
  const cancelFrame = ownerWindow.cancelAnimationFrame.bind(ownerWindow);
  const cancelTimer = globalThis.clearTimeout.bind(globalThis);
  const pending: {
    frameId?: number;
    timerId?: ReturnType<typeof setTimeout>;
    notified: boolean;
  } = { notified: false };
  const flush = () => {
    if (pending.notified) return;
    pending.notified = true;
    if (pending.frameId !== undefined) cancelFrame(pending.frameId);
    if (pending.timerId !== undefined) cancelTimer(pending.timerId);
    if (typeof window === "undefined" || window !== ownerWindow) return;
    notify();
  };
  pending.frameId = requestFrame(flush);
  if (!pending.notified) pending.timerId = setTimeout(flush, 100);
};

const createStore = (preloadedState?: Partial<RootState>) => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        apiSlice.middleware,
      ) as unknown as ReturnType<typeof getDefaultMiddleware>,
    devTools: import.meta.env.DEV,
    // CRITICAL Admin CI shard-05: jsdom can disappear before RTK's
    // frame fallback timer runs. Preserve frame ordering in tests and keep
    // ordinary app batching unchanged.
    // https://redux-toolkit.js.org/api/autoBatchEnhancer
    enhancers: (defaults) =>
      defaults(
        import.meta.env.VITEST
          ? {
              autoBatch: {
                type: "callback",
                queueNotification: queueTestFrameNotification,
              },
            }
          : undefined,
      ),
    preloadedState: preloadedState as RootState | undefined,
  });
};

export type AppStore = ReturnType<typeof createStore>;
export const makeStore: (preloadedState?: Partial<RootState>) => AppStore =
  createStore;
export const store: AppStore = makeStore();

// Enable automatic refetching on focus/reconnect
setupListeners(store.dispatch);

export type AppDispatch = AppStore["dispatch"];

// Typed hooks
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
